<script setup lang="ts">import { ref as _ref } from 'vue';

import { watch, ref, computed } from "vue";

import { caseFileService } from "@/services";
import { useMetadataStore } from "@/stores/metadata-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { UnPasswordResultStatus } from "@/models/UnPasswordResultStatus";

import type { Metadata } from "@/models/Metadata";
import type UnPasswordResult from "@/models/UnPasswordResult";
import { useCaseFileStore } from "@/stores/case-file-store";

const emit = defineEmits<(e: "resultReceived", result: UnPasswordResult) => void>();
withDefaults(defineProps<{
  file: Metadata;
}>(), {  })

const routeParams = useCaseRouteParams();

let status = _ref<"PASSWORD_INCORRECT" | "PASSWORD_PROTECTED" | "PASSWORD_ERROR" | "PASSWORD_PENDING">(
  "PASSWORD_PROTECTED",
);
const password = ref("");
let isWorking = _ref<boolean>(false);

const fileName = computed(() => useCaseFileStore().currentFile?.displayName);

watch(
  () => __props.file,
  (to) => {
    if (!to.passwordStatus) {
      return;
    }

    switch (to.passwordStatus) {
      case "passwordprotected":
        status.value = "PASSWORD_PROTECTED";
        break;
      case "incorrectpassword":
        status.value = "PASSWORD_INCORRECT";
        break;
      case "pending":
        status.value = "PASSWORD_PENDING";
        break;
    }
  },
  { immediate: true, deep: true },
);

async function submitPassword() {
  isWorking.value = true;
  try {
    const result = await caseFileService.unPassword(
      routeParams.value.client,
      routeParams.value.brand,
      routeParams.value.casename,
      __props.file.fileReference,
      { password: password.value },
    );
    // Check what the response was
    switch (result.status) {
      case UnPasswordResultStatus.Error:
        status.value = "PASSWORD_ERROR";
        break;
      case UnPasswordResultStatus.Incorrect:
        status.value = "PASSWORD_INCORRECT";
        break;
      case UnPasswordResultStatus.Pending:
        status.value = "PASSWORD_PENDING";
        useMetadataStore().passwordStatusPending();
        break;
      default:
        emit("resultReceived", result);
    }
  } catch (e) {
    console.error(e);
    status.value = "PASSWORD_ERROR";
  } finally {
    isWorking.value = false;
  }
}
function refresh(): Promise<void> {
  return useMetadataStore().refresh();
}
</script>
<template>
  <div class="tw-p-3">
    <h3 class="tw-my-3 tw-text-lg">
      This file <span v-if="fileName != null" class="tw-sr-only">({{ fileName }})</span> is currently protected by a
      password. Please submit your password to view this file.
    </h3>
    <div v-if="status === 'PASSWORD_PENDING'">
      <p class="tw-text-lg">Verifying password. Please wait..</p>
      <p class="tw-mt-2">
        You can click
        <a class="tw-font-semibold" href="#" @click.prevent="refresh">here</a>
        to refresh the case
      </p>
    </div>
    <div v-else>
      <form @submit.prevent="submitPassword">
        <input
          v-model="password"
          type="password"
          placeholder="Password"
          class="tw-flex-grow tw-rounded-l tw-border-l tw-p-1 tw-pr-4"
        />
        <button
          :disabled="isWorking || !password.trim()"
          type="submit"
          class="tw-rounded-r tw-bg-gray-700 tw-px-4 tw-py-1 tw-font-bold tw-text-white hover:tw-bg-gray-800"
          :class="isWorking || !password.trim() ? 'tw-cursor-not-allowed tw-opacity-50' : ''"
        >
          {{ isWorking ? "Processing" : "Submit" }}
        </button>
      </form>
      <p v-if="status === 'PASSWORD_INCORRECT'" class="tw-mt-2 tw-text-red-700">
        Your last entered password is incorrect
      </p>
      <p v-if="status === 'PASSWORD_ERROR'" class="tw-mt-2 tw-text-red-700">
        Something went wrong while trying to unlock the file, please re-type the password and try again
      </p>
    </div>
  </div>
</template>
